import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack,
  FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Alert
} from "@mui/material";
import dayjs from "dayjs";

const empty = {
  name: "",
  base: "",
  defaultRate: 0,
  startDate: "",
  endDate: "",
  capacity: 0,
  allocated: 0,
  commissionable: false,
  productType: "",
  notes: "",
  residential: true,
};

export default function AddEditProductDialog({
  open,
  onClose,
  onSave,
  initial,
  productTypeOptions = [],
  campuses = [],
  campusSessions = [],
}) {

    const isEdit = !!initial?.id;

    const initialState = useMemo(() => {
        if (!initial) return { ...empty };
        return {
            id: initial.id,
            name: initial.name ?? "",
            base: initial.base ?? "",
            defaultRate: initial.defaultRate ?? 0,
            startDate: initial.startDate ?? "",
            endDate: initial.endDate ?? "",
            capacity: initial.capacity ?? 0,
            allocated: initial.allocated ?? 0,
            commissionable: !!initial.commissionable,
            productType: initial.productType ?? "",
            notes: initial.notes ?? "",
            residential: initial.residential !== false,
        };
    }, [initial]);

    const [form, setForm] = useState(initialState);

    const [singleDay, setSingleDay] = useState(() => {
        if (!initial) return true;

        const s = initial.startDate ?? "";
        const e = initial.endDate ?? "";

        // If end missing, or same day -> treat as single day
        if (!e) return true;
        return s && e && s === e;
    });


    useEffect(() => {
        setForm(initialState);

        if (!initial) {
            setSingleDay(true);
        } else {
            const s = initialState.startDate || "";
            const e = initialState.endDate || "";
            setSingleDay(!e || (s && e && s === e));
        }
    }, [initialState, open]);


    const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

    const isResidential =
        String(form.productType).toUpperCase() === "RESIDENTIAL" ||
        String(form.productType) === "0";

    useEffect(() => {
        if (!open) return;

        if (isResidential && singleDay) {
            setSingleDay(false);

            // if endDate is empty or equal to start, nudge it forward 1 day
            if (form.startDate && (!form.endDate || form.endDate === form.startDate)) {
                const nextDay = dayjs(form.startDate).add(1, "day").format("YYYY-MM-DD");
                set("endDate", nextDay);
            }
        }
    }, [open, isResidential, singleDay, form.startDate, form.endDate]);

    const isSameDay = form.startDate && form.endDate && form.startDate === form.endDate;

    const dateError =
        isResidential && isSameDay
        ? "Residential products must end after the start date (use end date = next day for a 1-night product)."
        : "";

    const baseSessionWarning = useMemo(() => {
        if (!form.base || !form.startDate || !form.endDate) return "";
        if (!Array.isArray(campusSessions) || campusSessions.length === 0) return "";

        const productStart = dayjs(form.startDate);
        const productEnd = dayjs(form.endDate);
        if (!productStart.isValid() || !productEnd.isValid()) return "";

        const sessions = campusSessions.filter(
            (s) => String(s.name || "").trim() === form.base.trim()
        );
        if (sessions.length === 0) {
            return `${form.base} has no sessions on record — this product won't be bookable until a session is added.`;
        }

        const overlaps = sessions.some((s) => {
            const sessStart = dayjs(s.arrivalDate);
            const sessEnd = dayjs(s.departureDate);
            if (!sessStart.isValid() || !sessEnd.isValid()) return false;
            return (
                !productStart.isAfter(sessEnd, "day") &&
                !productEnd.isBefore(sessStart, "day")
            );
        });

        if (!overlaps) {
            return `${form.base} has no active session overlapping these dates. This product won't be bookable until a matching session is added.`;
        }
        return "";
    }, [form.base, form.startDate, form.endDate, campusSessions]);

    const canSave =
        form.name.trim().length > 0 &&
        form.base.trim().length > 0 &&
        form.startDate &&
        form.endDate &&
        Number.isFinite(Number(form.capacity)) &&
        Number(form.capacity) >= 0 &&
        !dateError;

    const handleSave = async () => {
        const payload = {
            ...(isEdit ? { id: form.id } : {}),
            name: form.name.trim(),
            base: form.base.trim(),
            defaultRate: Number(form.defaultRate) || 0,
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            capacity: parseInt(form.capacity, 10) || 0,
            allocated: parseInt(form.allocated, 10) || 0,
            commissionable: !!form.commissionable,
            productType: form.productType || null,
            notes: form.notes ?? "",
            residential: String(form.productType || "").toUpperCase() === "RESIDENTIAL"
                ? !!form.residential
                : true,
        };

        await onSave(payload);
    };

  return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? "Edit Product" : "Add Product"}</DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Name"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel id="product-base-label">Base</InputLabel>
                        <Select
                            labelId="product-base-label"
                            label="Base"
                            value={form.base}
                            onChange={(e) => set("base", e.target.value)}
                        >
                            {campuses.map((c) => (
                                <MenuItem key={c.id} value={c.name}>
                                    {c.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {baseSessionWarning && (
                        <Alert severity="warning" variant="outlined">
                            {baseSessionWarning}
                        </Alert>
                    )}

                    <FormControl fullWidth>
                        <InputLabel id="product-type-label">Product Type</InputLabel>
                        <Select
                            labelId="product-type-label"
                            label="Product Type"
                            value={form.productType}
                            onChange={(e) => set("productType", e.target.value)}
                        >
                            {productTypeOptions.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {String(t).toUpperCase() === "RESIDENTIAL" ? "BOOKING" : t}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {String(form.productType || "").toUpperCase() === "RESIDENTIAL" && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.residential}
                                    onChange={(e) => set("residential", e.target.checked)}
                                />
                            }
                            label="Residential"
                        />
                    )}

                    <Stack spacing={1}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={singleDay}
                                    disabled={isResidential}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSingleDay(checked);

                                        // If switching to single day, sync endDate to startDate
                                        if (checked) {
                                            set("endDate", form.startDate || "");
                                        } else {
                                            // If switching to multi-day and endDate is empty, default to startDate
                                            if (!form.endDate && form.startDate) {
                                                set("endDate", form.startDate);
                                            }
                                        }
                                    }}
                                />
                            }
                            label="Single day product"
                        />

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Start date"
                                type="date"
                                value={form.startDate}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    set("startDate", v);
                                    if (singleDay) set("endDate", v);
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                error={!!dateError}
                                helperText={dateError || ""}
                            />

                            {!singleDay && (
                                <TextField
                                    label="End date"
                                    type="date"
                                    value={form.endDate}
                                    onChange={(e) => set("endDate", e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            )}
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Default rate"
                            type="number"
                            value={form.defaultRate}
                            onChange={(e) => set("defaultRate", e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Capacity"
                            type="number"
                            value={form.capacity}
                            onChange={(e) => set("capacity", e.target.value)}
                            fullWidth
                        />
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Allocated"
                            type="number"
                            value={form.allocated}
                            fullWidth
                            disabled
                            helperText="Allocated is set by bookings."
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.commissionable}
                                    onChange={(e) => set("commissionable", e.target.checked)}
                                />
                            }
                            label="Commissionable"
                        />
                    </Stack>

                    <TextField
                        label="Notes"
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={!canSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
